<template>
  <div>
    <div
      v-if="label_only"
      class="filter-item"
      :data-testid="'filter-item-type-' + filter.type"
    >
      <div class="filter-item__field filter-item__field--label-only">
        {{ label }}:
      </div>
      <v-btn
        v-if="label_only && !non_deletable"
        icon
        variant="text"
        density="compact"
        size="small"
        class="filter-item__clear-btn"
        :aria-label="$t('settings_project.btn_clear')"
        @click="clear"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>
    <div
      v-else
      class="filter-item"
      :data-testid="'filter-item-type-' + filter.type"
    >
      <div class="filter-item__field">
        <v-switch
          v-if="toggleOption"
          class="filter-item__switch"
          inset
          :model-value="value === 'Yes'"
          @update:model-value="toggleSwitch"
          :label="label"
          :disabled="non_togglable"
          color="primary"
          hide-details
        ></v-switch>
        <search-label-autocomplete
          v-else-if="label_options"
          :field_label="label"
          :prepend_icon="null"
          :placeholder="$t('filter_tree.label_search_placeholder')"
          :value="value"
          :clear_on_select="false"
          @label-selected="on_filter_label_select"
          @cleared="on_filter_label_cleared"
        ></search-label-autocomplete>
        <autocomplete
          v-else-if="computedOptions && computedOptions.length > 0"
          :value="value"
          :field_label="label"
          :placeholder="label"
          :options="computedOptions"
          @select="set_value"
        ></autocomplete>
        <v-menu
          v-else-if="is_date"
          ref="dialog"
          v-model="modal"
          persistent
          location="bottom"
          :width="290"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-text-field
              :model-value="value"
              :label="label"
              prepend-icon=""
              readonly
              rounded
              variant="outlined"
              density="compact"
              hide-details
              v-bind="props"
            ></v-text-field>
          </template>
          <v-date-picker
            :model-value="value || undefined"
            color="primary"
            @update:model-value="set_value_on_date"
          >
            <template #actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" variant="text" @click="modal = false">Cancel</v-btn>
            </template>
          </v-date-picker>
        </v-menu>
        <v-text-field
          v-else-if="input"
          rounded
          :model-value="value"
          :label="label"
          density="compact"
          variant="outlined"
          hide-details
          @update:model-value="set_value_on_input"
        ></v-text-field>
      </div>
      <v-btn
        v-if="!non_deletable"
        icon
        variant="text"
        density="compact"
        size="small"
        class="filter-item__clear-btn"
        :aria-label="$t('settings_project.btn_clear')"
        @click="clear"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import Autocomplete from "@/components/Autocomplete";
import SearchLabelAutocomplete from "@/components/SearchLabelAutocomplete.vue";

export default {
  name: "FilterItem",
  components: { SearchLabelAutocomplete, Autocomplete },
  props: {
    filter: {required: true},
    label: {required: true},
    options: {default: false},
    input: {default: false},
    value: {default: ''},
    is_date: {default: ''},
    label_options: {default: false},
    non_deletable: {default: false},
    non_togglable: {default: false}
  },
  data() {
    return {
      modal: false,
    }
  },
  methods: {
    toggleSwitch(value) {
      if (this.non_togglable) {
        return;
      }
      if (value) {
        this.set_value('Yes')
      } else {
        this.set_value('No')
      }
    },
    clear() {
      if(!this.toggleOption && this.input) {
        if(this.value!== '') {
          this.set_value('')
          return;
        }
      }
      this.$emit('clear')
    },
    set_value(value) {
      if (value != null && typeof value === 'object') {
        value = value.label ?? value.name ?? value.value ?? '';
      }
      if (!value) {
        value = '';
      }
      this.$emit('set_value', value);
    },
    set_value_on_input(value) {
      this.$emit('set_value', value);
    },
    set_value_on_date(value) {
      if (value == null || value === "") {
        this.$emit("set_value", "");
        this.modal = false;
        return;
      }
      let normalized = value;
      if (typeof value === "string") {
        normalized = value.includes("T") ? value.slice(0, 10) : value;
      } else if (value instanceof Date) {
        normalized = value.toISOString().slice(0, 10);
      }
      this.$emit("set_value", normalized);
      this.modal = false;
    },
    on_filter_label_select(selected) {
      if (!selected) {
        return;
      }
      const name = typeof selected === 'string' ? selected : selected.name;
      this.set_value(name || '');
    },
    on_filter_label_cleared() {
      this.set_value('');
    },
  },
  computed: {
    label_only() {
      if (this.label_options) {
        return false;
      }
      return this.computedOptions.length === 0 && !this.input && this.label
    },
    toggleOption() {
      return this.computedOptions[0] === 'Yes' && this.computedOptions[1] === 'No';
    },
    computedOptions() {
      return this.options || [];
    }
  }
}
</script>

<style scoped>
.filter-item {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 10px;
}

.filter-item__field {
  flex: 1;
  min-width: 0;
}

.filter-item__field--label-only {
  padding-top: 6px;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.filter-item__switch {
  margin: 0;
}

.filter-item__clear-btn {
  flex-shrink: 0;
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.filter-item__clear-btn:hover {
  color: rgba(var(--v-theme-on-surface), 0.7);
  background: rgba(var(--v-theme-on-surface), 0.06);
}
</style>
